import { userClient } from '../services/api.service';
import {
  userProfileResponseSchema,
  updateUserProfileRequestSchema,
  type UpdateUserProfileRequest,
  type UserProfile,
} from '../validation/userProfileSchemas';
import {
  createUserProfileRequestSchema,
  registrationResultSchema,
  type CreateUserProfileRequest,
  type RegistrationResult,
} from '../validation/authSchemas';

/** Response from GET /api/v1/users/me/clinic-staff-approval-status */
export interface ClinicStaffApprovalStatus {
  isClinicStaff: boolean;
  isApprovedByProvider: boolean;
  /** Approved | Pending | Rejected – used to show pending vs rejected message on clinic-staff-pending-approval page */
  approvalStatus?: 'Approved' | 'Pending' | 'Rejected';
}

export const UserProfileApi = {
  /**
   * Get user profile by ID (numeric user ID)
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users)
   */
  async get(userId: number | string): Promise<UserProfile | null> {
    const { data } = await userClient.get(`/api/v1/users/${userId}`);
    const parsed = userProfileResponseSchema.parse(data);
    return parsed.data ?? null;
  },

  /**
   * Get user profile by Entra Object ID
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users/entra/{entraObjectId})
   * @param entraObjectId - Entra Object ID (UUID string)
   * @param options - Optional request options (e.g., headers for correlation ID)
   * @returns User profile if found, throws error if API call fails (404, 403, 500, etc.)
   */
  async getByEntraObjectId(
    entraObjectId: string,
    options?: { headers?: Record<string, string> }
  ): Promise<UserProfile | null> {
    try {
      const { data } = await userClient.get(`/api/v1/users/entra/${entraObjectId}`, {
        headers: options?.headers,
      });
      const parsed = userProfileResponseSchema.parse(data);
      return parsed.data ?? null;
    } catch (error: any) {
      // Re-throw the error so it can be caught and classified by completeLogin
      // This ensures 404, 403, 500, etc. are properly handled
      throw error;
    }
  },

  /**
   * Get user profile by email address
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users/email/{email})
   * Falls back to check-email and username endpoints if needed
   * @param email - User email address
   * @returns User profile if found, null otherwise
   */
  async getByEmail(email: string): Promise<UserProfile | null> {
    try {
      // Priority 1: Use dedicated email endpoint (path parameter - cleaner)
      const { data } = await userClient.get(`/api/v1/users/email/${encodeURIComponent(email)}`);
      const parsed = userProfileResponseSchema.parse(data);
      return parsed.data ?? null;
    } catch (error: any) {
      // If email endpoint fails with 404, try check-email endpoint (query parameter)
      if (error?.response?.status === 404) {
        try {
          const { data } = await userClient.get(`/api/v1/users/check-email?email=${encodeURIComponent(email)}`);
          const parsed = userProfileResponseSchema.parse(data);
          return parsed.data ?? null;
        } catch (checkEmailError: any) {
          // If check-email also fails, try username endpoint (email might be username)
          if (checkEmailError?.response?.status === 404) {
            try {
              const { data } = await userClient.get(`/api/v1/users/username/${encodeURIComponent(email)}`);
              const parsed = userProfileResponseSchema.parse(data);
              return parsed.data ?? null;
            } catch (usernameError: any) {
              if (usernameError?.response?.status === 404) {
                return null;
              }
              throw usernameError;
            }
          }
          throw checkEmailError;
        }
      }
      throw error;
    }
  },

  /**
   * Get clinic-staff approval status for the current user.
   * Requires Authorization and X-Selected-Role header (active role code, e.g. OfficeAdmin, PreExamPreparer).
   * Uses VITE_API_USER_BASE_URL (User Service).
   */
  async getClinicStaffApprovalStatus(activeRole: string): Promise<ClinicStaffApprovalStatus> {
    const { data } = await userClient.get<{ success: boolean; data?: ClinicStaffApprovalStatus }>(
      '/api/v1/users/me/clinic-staff-approval-status',
      { headers: { 'X-Selected-Role': activeRole } }
    );
    if (!data?.success || data.data == null) {
      return { isClinicStaff: false, isApprovedByProvider: false, approvalStatus: 'Pending' };
    }
    return data.data;
  },

  /**
   * Update user profile by ID or Entra Object ID
   * Automatically detects if userId is a UUID and uses the appropriate endpoint
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users)
   * - For numeric IDs: PUT /api/v1/users/{id}
   * - For UUIDs (Entra Object ID): PUT /api/v1/users/entra/{entraObjectId}
   */
  async update(userId: number | string, payload: UpdateUserProfileRequest): Promise<UserProfile | null> {
    const body = updateUserProfileRequestSchema.parse(payload);
    
    // Check if userId is a UUID (Entra Object ID format)
    const isUUID = typeof userId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
    
    // Use the appropriate endpoint based on ID type
    const endpoint = isUUID 
      ? `/api/v1/users/entra/${userId}`  // Entra Object ID endpoint
      : `/api/v1/users/${userId}`;        // Numeric ID endpoint
    
    const { data } = await userClient.put(endpoint, body);
    const parsed = userProfileResponseSchema.parse(data);
    return parsed.data ?? null;
  },

  /**
   * Create a new user (registration)
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users)
   */
  async create(payload: CreateUserProfileRequest, accessToken?: string): Promise<RegistrationResult> {
    const body = createUserProfileRequestSchema.parse(payload);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const { data } = await userClient.post('/api/v1/users', body, { headers });

    return registrationResultSchema.parse(data);
  },

  /**
   * Get clinician profile by user ID
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users/{id}/clinician-profile)
   * @param userId - Numeric user ID
   * @returns Clinician profile if found, null if not found (404) or error
   */
  async getClinicianProfile(userId: number): Promise<{
    clinicianProfileId: number;
    userId: number;
    npiNumber: number | null;
    clinicianProfileSpecificDetails: string | null;
    deaRegistration?: { number: string; expirationDate: string | null }[];
    boardCertification?: { name: string; expirationDate: string | null }[];
    additionalDetails?: { additionalCredentials: string | null };
    medicalLicenses?: { clinicianMedicalLicenseId: number; number: string; state: string; expirationDate: string | null }[];
    specialties?: { id: number; name: string }[];
    createdDate: string;
    createdBy: string | number;
    updatedDate: string | null;
    updatedBy: string | null;
  } | null> {
    try {
      const { data } = await userClient.get(`/api/v1/users/${userId}/clinician-profile`);
      
      // Handle response structure: { success, data, message, errors, metadata }
      if (data?.success && data?.data) {
        return data.data;
      }
      
      return null;
    } catch (error: any) {
      // Handle 404 as "record doesn't exist" - this is expected
      if (error?.response?.status === 404) {
        console.log('[UserProfileApi] Clinician profile not found (404) - record does not exist');
        return null;
      }
      
      // For other errors, log and throw
      console.error('[UserProfileApi] Error getting clinician profile:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  },

  /**
   * Returns true if the clinician profile has license data (new structured fields or legacy clinicianProfileSpecificDetails).
   * Use this for redirect/license-complete checks so both new model (medicalLicenses, specialties, additionalDetails)
   * and legacy clinicianProfileSpecificDetails are recognized.
   */
  hasLicenseDataInClinicianProfile(profile: {
    clinicianProfileSpecificDetails?: string | null;
    medicalLicenses?: unknown[];
    specialties?: unknown[];
    additionalDetails?: { additionalCredentials?: string | null };
  } | null): boolean {
    if (!profile) return false;
    const hasNewShapeData =
      (Array.isArray(profile.medicalLicenses) && profile.medicalLicenses.length > 0) ||
      (Array.isArray(profile.specialties) && profile.specialties.length > 0) ||
      (profile.additionalDetails?.additionalCredentials != null && profile.additionalDetails.additionalCredentials !== '');
    const hasLegacyData =
      profile.clinicianProfileSpecificDetails != null && profile.clinicianProfileSpecificDetails.trim() !== '';
    return hasNewShapeData || hasLegacyData;
  },

  /**
   * Create clinician profile (PUT)
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users/{id}/clinician-profile)
   * @param userId - Numeric user ID
   * @param payload - Payload with npiNumber, optional legacy clinicianProfileSpecificDetails, and new structured fields
   * @returns Created clinician profile
   */
  async createClinicianProfile(
    userId: number,
    payload: {
      npiNumber: number;
      clinicianProfileSpecificDetails?: string | null;
      medicalLicenses?: { number: string; state: string; expirationDate: string | null }[];
      deaRegistration?: { number: string; expirationDate: string | null }[];
      boardCertification?: { name: string; expirationDate: string | null }[];
      additionalDetails?: { additionalCredentials: string | null };
      specialtyIds?: number[];
    }
  ): Promise<any> {
    const { data } = await userClient.put(`/api/v1/users/${userId}/clinician-profile`, payload);

    if (data?.success && data?.data) {
      return data.data;
    }
    return data;
  },

  /**
   * Update clinician profile (PATCH)
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/users/{id}/clinician-profile)
   * @param userId - Numeric user ID
   * @param payload - Payload with npiNumber and/or structured license fields
   * @returns Updated clinician profile
   */
  async updateClinicianProfile(
    userId: number,
    payload: {
      npiNumber?: number;
      clinicianProfileSpecificDetails?: string | null;
      medicalLicenses?: { number: string; state: string; expirationDate: string | null }[];
      deaRegistration?: { number: string; expirationDate: string | null }[];
      boardCertification?: { name: string; expirationDate: string | null }[];
      additionalDetails?: { additionalCredentials: string | null };
      specialtyIds?: number[];
    }
  ): Promise<any> {
    const { data } = await userClient.patch(`/api/v1/users/${userId}/clinician-profile`, payload);

    if (data?.success && data?.data) {
      return data.data;
    }
    return data;
  },

  /**
   * Save license data - helper that uses PUT (create) or PATCH (update)
   * Sends structured payload: medicalLicenses, deaRegistration, boardCertification, additionalDetails, specialtyIds.
   * @param userId - Numeric user ID
   * @param licenseFormData - Form data including optional specialtyIds (catalog IDs)
   * @param npiNumber - NPI number as string (converted to number)
   * @returns Saved clinician profile data
   */
  async saveLicenseData(
    userId: number,
    licenseFormData: {
      licenseNumber: string;
      licenseState: string;
      licenseExpirationDate: string;
      deaNumber: string;
      deaExpirationDate: string;
      boardCertification: string;
      certificationExpirationDate: string;
      specialties: string[];
      specialtiesCode?: string[];
      additionalCredentials: string;
      /** Selected specialty catalog IDs to send to API */
      specialtyIds?: number[];
    },
    npiNumber: string | null
  ): Promise<any> {
    const formatDateForJson = (dateStr: string): string | null => {
      if (!dateStr || !dateStr.trim()) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        if (month && day && year) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      return null;
    };

    const npiNumberInt = npiNumber ? parseInt(npiNumber, 10) : 0;
    if (isNaN(npiNumberInt)) {
      console.warn('[UserProfileApi] Invalid NPI number, using 0:', npiNumber);
    }

    const medicalLicenses =
      licenseFormData.licenseNumber?.trim() || licenseFormData.licenseState?.trim()
        ? [
            {
              number: (licenseFormData.licenseNumber ?? '').trim(),
              state: (licenseFormData.licenseState ?? '').trim(),
              expirationDate: formatDateForJson(licenseFormData.licenseExpirationDate ?? ''),
            },
          ]
        : [];

    const deaRegistration =
      licenseFormData.deaNumber?.trim()
        ? [
            {
              number: licenseFormData.deaNumber.trim(),
              expirationDate: formatDateForJson(licenseFormData.deaExpirationDate ?? ''),
            },
          ]
        : [];

    const boardCertification =
      licenseFormData.boardCertification?.trim()
        ? [
            {
              name: licenseFormData.boardCertification.trim(),
              expirationDate: formatDateForJson(licenseFormData.certificationExpirationDate ?? ''),
            },
          ]
        : [];

    const additionalDetails = {
      additionalCredentials: licenseFormData.additionalCredentials?.trim() || null,
    };

    const payload = {
      npiNumber: npiNumberInt,
      medicalLicenses,
      deaRegistration,
      boardCertification,
      additionalDetails,
      specialtyIds: licenseFormData.specialtyIds ?? [],
    };

    const existingProfile = await this.getClinicianProfile(userId);

    if (!existingProfile) {
      console.log('[UserProfileApi] Clinician profile does not exist, creating with PUT');
      return await this.createClinicianProfile(userId, payload);
    }

    console.log('[UserProfileApi] Clinician profile exists, updating with PATCH');
    return await this.updateClinicianProfile(userId, payload);
  },

  /**
   * Get provider license information
   * Uses VITE_API_USER_BASE_URL (e.g., https://localhost:7006/api/v1/license)
   * The endpoint may use the authenticated user from the token, or accept userId as path parameter
   * @param userId - User ID or Entra Object ID (optional, may be determined from token)
   * @param accessToken - Access token for authorization (required)
   * @returns License data if exists, null if not found or error
   */
  async getLicense(userId?: number | string, accessToken?: string): Promise<any | null> {
    try {
      if (!accessToken) {
        console.warn('[UserProfileApi] No access token provided for license check');
        return null;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      
      // Try endpoint with userId if provided, otherwise try without (token-based)
      let endpoint = '/api/v1/license';
      if (userId) {
        endpoint = `/api/v1/license/${userId}`;
      }
      
      console.log('[UserProfileApi] Checking license at endpoint:', endpoint, 'with userId:', userId);
      
      const { data } = await userClient.get(endpoint, { headers });
      
      console.log('[UserProfileApi] License API response:', data);
      
      // Return the license data if it exists and has content
      if (data) {
        // Handle different response structures
        const licenseData = data.data || data;
        if (licenseData && (typeof licenseData === 'object' ? Object.keys(licenseData).length > 0 : licenseData)) {
          console.log('[UserProfileApi] License data found:', licenseData);
          return licenseData;
        }
      }
      
      console.log('[UserProfileApi] No license data in response');
      return null;
    } catch (error: any) {
      // Handle 404 as "license doesn't exist" - this is expected for providers without license
      if (error?.response?.status === 404) {
        console.log('[UserProfileApi] License not found (404) - user has not completed license');
        return null;
      }
      
      // For other errors, log details and return null
      console.error('[UserProfileApi] Error checking license:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message,
        endpoint: userId ? `/api/v1/license/${userId}` : '/api/v1/license',
      });
      return null;
    }
  },

  /**
   * Check if NPI number is already registered in the database against a provider
   * Uses GET endpoint: /api/v1/users/check-npi?npiNumber={npiNumber}&firstName={firstName}&lastName={lastName}
   * @param npiNumber - NPI number to check (10-digit string)
   * @param firstName - Provider's first name to match
   * @param lastName - Provider's last name to match
   * @returns true if NPI is already registered, false otherwise
   */
  async checkNpiExists(npiNumber: string, firstName: string, lastName: string): Promise<boolean> {
    try {
      // Validate inputs
      if (!npiNumber || !firstName || !lastName) {
        console.warn('[UserProfileApi] Missing required parameters for NPI check:', {
          hasNpi: !!npiNumber,
          hasFirstName: !!firstName,
          hasLastName: !!lastName,
        });
        return false;
      }

      // Convert NPI to number (backend expects numeric value)
      const trimmedNpi = npiNumber.trim();
      const npiNumberInt = parseInt(trimmedNpi, 10);
      
      if (isNaN(npiNumberInt) || npiNumberInt <= 0) {
        console.warn('[UserProfileApi] Invalid NPI number format:', trimmedNpi);
        throw new Error('Invalid NPI number format. NPI must be a valid 10-digit number.');
      }

      // Build query parameters for GET request
      const queryParams = new URLSearchParams({
        npiNumber: npiNumberInt.toString(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      const endpoint = `/api/v1/users/check-npi?${queryParams.toString()}`;

      if (import.meta.env.DEV) {
        console.log('[UserProfileApi] Checking NPI existence:', {
          npiNumber: npiNumberInt,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          endpoint,
        });
      }

      // Call the GET endpoint with query parameters
      const response = await userClient.get(endpoint);
      
      if (import.meta.env.DEV) {
        console.log('[UserProfileApi] NPI check response:', {
          status: response.status,
          data: response.data,
          headers: response.headers,
        });
      }

      // Handle different possible response formats
      const data = response.data;
      
      // Log the full response for debugging
      if (import.meta.env.DEV) {
        console.log('[UserProfileApi] Full API response:', JSON.stringify(data, null, 2));
      }
      
      // First, check if response indicates an error (success: false)
      if (typeof data === 'object' && data !== null && 'success' in data && data.success === false) {
        // Extract error message from response
        let errorMessage = data.message || 'Validation failed. Please check your NPI number and try again.';
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          // Extract first validation error
          errorMessage = data.errors[0];
        }
        console.error('[UserProfileApi] API returned success: false:', {
          message: errorMessage,
          errors: data.errors,
          data: data.data,
        });
        throw new Error(errorMessage);
      }
      
      // Case 1: Response has success property - check success and data
      if (typeof data === 'object' && data !== null && 'success' in data) {
        if (data.success === true) {
          // API returns: { success: true, data: { npiNumber: 1689330789, exists: false } }
          // Check if data.data exists and has an 'exists' property
          const dataValue = data.data;
          
          if (dataValue && typeof dataValue === 'object' && 'exists' in dataValue) {
            // API explicitly tells us if NPI exists via the 'exists' property
            const exists = Boolean(dataValue.exists);
            console.log('[UserProfileApi] NPI check result (exists property in data.data):', {
              exists,
              npiNumber: dataValue.npiNumber,
              message: data.message,
            });
            return exists;
          }
          
          // Fallback: if data.data is null or undefined, NPI doesn't exist
          // If data.data exists but no 'exists' property, check if it's a valid object
          const hasData = dataValue !== null && dataValue !== undefined;
          console.log('[UserProfileApi] NPI check result (success: true, no exists property):', {
            hasData,
            dataValue,
            dataType: typeof dataValue,
          });
          return hasData;
        }
        // success: false already handled above, but just in case
        console.log('[UserProfileApi] NPI check result (success: false):', false);
        return false;
      }
      
      // Case 2: Response has a boolean property (e.g., { exists: true/false } or { isRegistered: true/false })
      if (typeof data === 'object' && data !== null) {
        if ('exists' in data) {
          const exists = Boolean(data.exists);
          console.log('[UserProfileApi] NPI check result (exists property):', exists);
          return exists;
        }
        if ('isRegistered' in data) {
          const isRegistered = Boolean(data.isRegistered);
          console.log('[UserProfileApi] NPI check result (isRegistered property):', isRegistered);
          return isRegistered;
        }
      }
      
      // Case 3: Response is a boolean directly
      if (typeof data === 'boolean') {
        console.log('[UserProfileApi] NPI check result (boolean):', data);
        return data;
      }
      
      // Case 4: Response has data property that indicates existence
      if (data && typeof data === 'object' && 'data' in data) {
        const hasData = data.data !== null && data.data !== undefined;
        console.log('[UserProfileApi] NPI check result (data property):', hasData);
        return hasData;
      }
      
      // Case 5: Empty or null response means NPI doesn't exist
      // Don't treat any non-empty response as "exists" - be explicit
      console.log('[UserProfileApi] NPI not found (empty/null/unrecognized response)');
      return false;
    } catch (error: any) {
      const status = error?.response?.status;
      const errorData = error?.response?.data;
      
      // Handle 404 as "NPI doesn't exist" - this is expected and means NPI is available
      if (status === 404) {
        console.log('[UserProfileApi] NPI not found (404) - NPI is available for registration');
        return false;
      }
      
      // Handle 400 Bad Request (validation errors)
      if (status === 400) {
        // Extract validation error message from response
        let errorMessage = 'Invalid request. Please check your NPI number and try again.';
        
        if (errorData) {
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            // Extract first validation error
            errorMessage = errorData.errors[0];
          }
        }
        
        console.error('[UserProfileApi] Validation error checking NPI existence:', {
          status,
          errorMessage,
          errorData,
          npiNumber,
          firstName,
          lastName,
        });
        
        throw new Error(errorMessage);
      }
      
      // Handle 200 OK with error in body (some APIs return 200 with error message)
      if (status === 200 && errorData) {
        // Check if response indicates an error
        if (errorData.success === false || errorData.errors) {
          let errorMessage = errorData.message || 'Unable to verify NPI availability.';
          if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            errorMessage = errorData.errors[0];
          }
          console.warn('[UserProfileApi] Received 200 OK but with error in response:', errorData);
          throw new Error(errorMessage);
        }
        // If no error in response, treat as NPI not found
        console.log('[UserProfileApi] Received 200 OK - NPI not found');
        return false;
      }
      
      // For other errors (500, 405, etc.), log and throw to block registration
      // This ensures we don't allow duplicate NPIs if there's an API error
      console.error('[UserProfileApi] Error checking NPI existence:', {
        status,
        statusText: error?.response?.statusText,
        message: error?.message,
        errorData,
        npiNumber,
        firstName,
        lastName,
      });
      
      // Extract error message from response if available
      let errorMessage = `Unable to verify NPI availability. Please try again or contact support.`;
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0];
        }
      }
      
      // Fail closed: if we can't verify, assume NPI might exist and block registration
      // This is safer than allowing potential duplicates
      throw new Error(`${errorMessage} Status: ${status || 'Unknown'}`);
    }
  },
};


